"use client"
import { ChevronDown } from "lucide-react"
import { useEffect, useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { cn } from "@/lib/utils"
import Link from "next/link"
import { CategoryBanner } from "../category-banner"
import CartCount from "../cart"
import { MobileNav } from "./mobile-nav"
import { CategoryIcon } from "../icons/category-icon"
import { ArrowUpRight } from "../icons/arrow-up-right"
import { Search } from "../icons/search"
import { UserIcon } from "../icons/user-icon"
import { Input } from "@/components/ui/input"
import { MobileSearch } from "./mobile-search"
import { useDebounce } from "@/hooks/use-debounce"
import Image from "next/image"

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [term, setTerm] = useState(searchParams.get("q") || "") // Initialize with query param if available
  const debouncedTerm = useDebounce(term, 600)

  useEffect(() => {
  const utmSource = searchParams.get("utm_source");
  const fbclid = searchParams.get("fbclid");
  const gclid = searchParams.get("gclid");

  let data;

  if (utmSource && fbclid) {
    // Take everything from the URL
    data = {
      utm_source: utmSource,
      utm_medium: searchParams.get("utm_medium") ?? "",
      utm_campaign: searchParams.get("utm_campaign") ?? "",
      utm_content: searchParams.get("utm_content") ?? "",
    };
  } else if (fbclid) {
    // Only fbclid is present, use Instagram defaults
    data = {
      utm_source: "instagram",
      utm_medium: "organic",
      utm_campaign: "",
      utm_content: "ig_bio_traffic",
    };
  } else {
    // Fallback logic (e.g., Google or others)
    data = {
      utm_source: gclid
        ? "google"
        : utmSource ?? "",
      utm_medium: searchParams.get("utm_medium") ?? "",
      utm_campaign: searchParams.get("utm_campaign") ?? "",
      utm_content: gclid
        ? `gclid:${gclid}`
        : searchParams.get("utm_content") ?? "",
    };
  }

  if (!sessionStorage.getItem("utm_data")) {
    sessionStorage.setItem("utm_data", JSON.stringify(data));
  }
}, []);

  useEffect(() => {
    const query = debouncedTerm ? `?q=${encodeURIComponent(debouncedTerm)}` : "";
    if (!pathname.startsWith("/all-products") && query !== "") {
      router.replace(`/all-products${query}`);
    } else if (pathname.startsWith("/all-products")) {
      router.replace(`/all-products${query}`);
    }
  }, [debouncedTerm]);

  return (
    <>
      <CategoryBanner
        setIsOpen={setIsOpen}
        className={cn(
          isOpen ? "ranslate-y-[0%]" : "translate-y-[-100%]",
          "transition-all duration-200 z-[1000]"
        )}
      />
      <header className="fixed top-0 left-0 z-[1000] right-0 border-0 lg:border-b-2 bg-white font-gilroySemiBold border-[#E7E7E7] w-full h-16 lg:h-20 flex items-center justify-center">
        <nav className="flex w-[92%] sm:w-[94%] h-full justify-between items-center">
          <MobileNav />

          <Link
            href="/"
            className="lg:hidden -mr-[10%] sm:-mr-[8%] cursor-pointer  rounded-full font-orange flex justify-center items-center"
            aria-label="Home"
          >
            <Image
              width={36}
              height={36}
              loading="eager"
              src="/edify-logo.webp"
              alt="logo"
              className="size-9"
              priority
              sizes="(max-width: 768px) 36px, 36px"
              quality={85}
            />
          </Link>

          <ul className="hidden lg:flex items-center md:gap-x-2  select-none">
            <li className="flex-none">
              <Link
                prefetch={false}
                href="/"
                aria-label="Home"
                className="cursor-pointer flex justify-center items-center z-10 pr-4"
              >
                <Image
                  width={36}
                  height={36}
                  loading="eager"
                  src="/edify-logo.webp"
                  alt="logo"
                  className="size-9"
                  priority
                  sizes="(max-width: 768px) 36px, 36px"
                  quality={85}
                />
              </Link>
            </li>

            <li>
              <button
                onClick={() => setIsOpen((prev) => !prev)}
                onMouseEnter={() => setIsOpen((prev) => !prev)}
                className={cn(
                  "flex items-center gap-2 px-2.5 py-1.5 cursor-pointer group hover:bg-slate-50 rounded-md z-10",
                  isOpen ? "bg-slate-50" : ""
                )}
                aria-expanded={isOpen}
                aria-controls="category-menu"
              >
                <CategoryIcon /> Category{" "}
                <div className="relative h-3.5 w-4">
                  <ChevronDown
                    className="absolute size-4 inset-0 translate-y-[0%] opacity-100 group-hover:translate-y-[100%] group-hover:opacity-0 transition duration-200"
                    strokeWidth={2.5}
                  />
                  <ChevronDown
                    className="absolute size-4 inset-0 translate-y-[-100%] opacity-0 group-hover:translate-y-[0%] group-hover:opacity-100 transition duration-200"
                    strokeWidth={2.5}
                  />
                </div>
              </button>
            </li>

            <li>
              <Link
                prefetch={false}
                className="flex whitespace-nowrap items-center gap-2 group hover:bg-slate-50 px-2.5 py-1.5 rounded-md cursor-pointer z-10"
                href="/all-products"
              >
                All Products
                <ArrowUpRight className="size-4 pt-4 group-hover:pt-0 translate-y-[20%] translate-x-[-90%] group-hover:translate-x-[0%] group-hover:translate-y-[0%] transition-all duration-200" />
              </Link>
            </li>
            <li>
              <Link
                prefetch={false}
                className="flex items-center gap-2 group hover:bg-slate-50 px-2.5 py-1.5 rounded-md cursor-pointer z-10"
                href="/b2b"
              >
                Business{" "}
                <ArrowUpRight className="size-4 pt-4 group-hover:pt-0 translate-y-[20%] translate-x-[-90%] group-hover:translate-x-[0%] group-hover:translate-y-[0%] transition-all duration-200" />
              </Link>
            </li>
            <li className="relative">
              <Link
                prefetch={false}
                href="/about-us"
                className="flex items-center gap-2 group hover:bg-slate-50 px-2.5 py-1.5 rounded-md cursor-pointer whitespace-nowrap z-10"
              >
                About Us{" "}
                <ArrowUpRight className="size-4 pt-4 group-hover:pt-0 translate-y-[20%] translate-x-[-90%] group-hover:translate-x-[0%] group-hover:translate-y-[0%] transition-all duration-200" />
              </Link>
            </li>
            <li>
              <Link
                prefetch={false}
                href="https://deviceflow.ai/"
                target="_blank"
                className="flex items-center gap-2 group hover:bg-slate-50 px-2.5 py-1.5 rounded-md cursor-pointer whitespace-nowrap z-10"
              >
                Startups{" "}
                <ArrowUpRight className="size-4 pt-4 group-hover:pt-0 translate-y-[20%] translate-x-[-90%] group-hover:translate-x-[0%] group-hover:translate-y-[0%] transition-all duration-200" />
              </Link>
            </li>
            <li>
              <Link
                prefetch={false}
                href="https://edify.club/blog/"
                target="_blank"
                className="flex items-center gap-2 group hover:bg-slate-50 px-2.5 py-1.5 rounded-md cursor-pointer whitespace-nowrap z-10"
              >
                Blogs{" "}
                <ArrowUpRight className="size-4 pt-4 group-hover:pt-0 translate-y-[20%] translate-x-[-90%] group-hover:translate-x-[0%] group-hover:translate-y-[0%] transition-all duration-200" />
              </Link>
            </li>
          </ul>

          <ul className="flex items-center gap-2 lg:gap-3">
            <li className="group relative  w-full">
              <label htmlFor="search" className="sr-only">
                Search
              </label>
              <Input
                id="search"
                type="text"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Search"
                className="hidden lg:block rounded-3xl md:w-64 xl:w-80 bg-[#F0F0F0] placeholder:text-[#C5C5C5] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <Link
                prefetch={false}
                href="/all-products"
                type="button"
                className="hidden lg:flex lg:absolute lg:right-2 lg:top-1 text-sm size-9 lg:size-8 bg-black rounded-full items-center justify-center"
                aria-label="Search"
              >
                <Search />
              </Link>
            </li>

            {pathname !== "/" && (
              <li className="block lg:hidden relative w-full">
                <MobileSearch />
              </li>
            )}
            <li>
              <Link
                prefetch={false}
                href="/cart"
                className="bg-black relative p-2 rounded-full size-9 flex justify-center items-center cursor-pointer"
                aria-label="View Cart"
              >
                <CartCount />
              </Link>
            </li>
            <li className="hidden sm:block">
              <Link
                prefetch={false}
                href="/profile"
                className="bg-black p-2 rounded-full size-9 flex justify-center items-center cursor-pointer"
                aria-label="View Profile"
              >
                <UserIcon className="size-5" />
              </Link>
            </li>
          </ul>
        </nav>
      </header>
      <div className="h-16 lg:h-20 pointer-events-none w-full" />
    </>
  )
}
